import copyToClipboard from "copy-to-clipboard"
import { observer } from "mobx-react"
import { ContextMenu, ContextMenuAction } from "modules/common/components/context-menu/ContextMenu"
import { RemoveConfirmation } from "modules/common/components/form/RemoveConfirmation"
import { Loader, LocalLoader } from "modules/common/components/loader/Loader"
import { PlanrButton } from "modules/common/components/planr/button/Button"
import { EMPTY_OBJECT_ID } from "modules/common/constants"
import { routes } from "modules/common/routes"
import { useStableNavigate } from "modules/common/components/routing/useStableNavigate"
import {
  mapQuestion,
  MessageType,
  OrderQuestionSnapshotType,
  SaveQuestion,
} from "modules/orders-manage/models/order-question"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { DragDropContext, DropResult } from "@hello-pangea/dnd"
import { isDropDisabled, PortfolioSnapshotType, SaveColumn, SystemColumns } from "../models/board"
import { QuestionCardType } from "../models/card"
import { OrderQuestionStoreType } from "../models/store"
import { AddColumn } from "./AddColumn"
import { BoardColumnView } from "./BoardColumnView"
import { BoardFilters, ListViewToggle } from "./BoardFilters"
import { ColumnsDialog } from "./ColumnsDialog"
import { MoveChildrenDialog } from "./MoveChildrenDialog"
import { MoveToBoardDialog } from "./MoveToBoardDialog"
import { ReadinessConfirmDialog } from "./ReadinessConfirmDialog"
import { PortfolioBar } from "./PortfolioBar"
import { PortfolioDialog } from "./PortfolioDialog"
import { QuestionDialog } from "./QuestionDialog"
import { ReviewConfirmDialog } from "./ReviewConfirmDialog"
import styles from "./QuestionsBoard.module.scss"

/**
 * Иконка настройки колонок: доска контуром — полоса заголовков и три колонки. В наборе иконок
 * приложения такой нет — рисуем свою, цвет берётся от кнопки
 */
const ColumnsIcon = () => (
  <svg
    className="planr-icon"
    width="20"
    height="20"
    viewBox="0 0 16 16"
    // заливку и обводку — стилем: правила кнопок planr красят заливку иконок через CSS
    style={{ width: 20, height: 20, fill: "none", stroke: "currentColor", strokeWidth: 1.3, strokeLinecap: "round" }}
    aria-hidden={true}
  >
    <rect x="1.5" y="2.5" width="13" height="11" rx="2" />
    <path d="M1.5 5.5h13M5.9 5.5v8M10.1 5.5v8" />
  </svg>
)

export const QuestionsBoard: React.FC<QuestionsBoardProps> = observer((props) => {
  const {
    store,
    canManage,
    canAddQuestion,
    hideProject,
    currentUserId,
    orderGip,
    onShowAll,
    linkedQuestionId,
    onQuestionOpened,
    onQuestionClosed,
    compact,
  } = props

  const navigate = useStableNavigate()

  // на странице трекера поиск идёт по всем доскам — как в Jira: набор сразу открывает список.
  // Во вкладке договора и в виджете главной поиск остаётся поиском по доске
  const searchAll = !compact && !hideProject
  const openSearch = useCallback(
    (value: string) => {
      value.trim()
        ? navigate(routes.orders.orderQuestionsList() + `#q=${encodeURIComponent(value)}`)
        : store.setQuery(value)
    },
    [navigate, store]
  )
  const [editable, setEditable] = useState<SaveQuestion | null>(null)
  const [current, setCurrent] = useState<OrderQuestionSnapshotType | null>(null)
  const [columnsDialog, setColumnsDialog] = useState(false)
  const [portfolioDialog, setPortfolioDialog] = useState<{ open: boolean; value: PortfolioSnapshotType | null }>({
    open: false,
    value: null,
  })
  const [dragging, setDragging] = useState<QuestionCardType | null>(null)
  const [reviewConfirm, setReviewConfirm] = useState<ReviewConfirm | null>(null)
  const [menu, setMenu] = useState<CardMenu | null>(null)
  const [readinessConfirm, setReadinessConfirm] = useState<QuestionCardType | null>(null)
  const [moveTarget, setMoveTarget] = useState<QuestionCardType | null>(null)
  // доска и колонка уже выбраны, остаётся решить судьбу подзаданий
  const [childrenConfirm, setChildrenConfirm] = useState<MoveRequest | null>(null)
  /** «Отобразить все» нажали: страница перезагружается, кнопку убираем сразу */
  const [showAllPressed, setShowAllPressed] = useState(false)
  /** Карточка, к которой доска прокручивается и подсвечивает её: после закрытия задания */
  const [revealed, setRevealed] = useState("")
  const revealTimer = useRef(0)

  const columns = store.columns
  // считает стор: useMemo не пересчитывался бы — ссылка на массив меток не меняется
  const tagColors = store.tagColors

  const gip = useMemo(() => {
    if (!store.filtersOrderId) {
      return orderGip
    }

    const order = store.orders.orders.find((o) => o.id === store.filtersOrderId)
    return order?.employer?.id ?? orderGip
  }, [store.filtersOrderId, store.orders.orders, orderGip])

  /** Показываем карточку на доске: прокручиваем к ней колонку и подсвечиваем */
  const revealCard = useCallback((id: string) => {
    setRevealed(id)

    revealTimer.current && window.clearTimeout(revealTimer.current)
    revealTimer.current = window.setTimeout(() => setRevealed(""), 2500)
  }, [])

  useEffect(
    () => () => {
      revealTimer.current && window.clearTimeout(revealTimer.current)
    },
    []
  )

  const openQuestion = useCallback(
    async (question: { id: string }) => {
      store.setLoading(true)
      const loaded = await store.getById(question.id)
      store.setLoading(false)

      // задание удалено или недоступно: адрес возвращаем к доске, иначе ссылка залипнет
      if (!loaded) {
        onQuestionClosed && onQuestionClosed()
        return
      }

      setCurrent(store.question)
      setEditable(mapQuestion(store.question, gip))

      // адрес открытого задания виден в строке браузера: такой ссылкой можно поделиться
      onQuestionOpened && onQuestionOpened(question.id)
    },
    [store, gip, onQuestionOpened, onQuestionClosed]
  )

  const openNew = useCallback(
    (columnId?: string, parent?: OrderQuestionSnapshotType) => {
      const empty = store.emptyQuestion(columnId)

      setCurrent(empty)
      setEditable(mapQuestion(empty, gip, parent))
    },
    [store, gip]
  )

  const closeDialog = useCallback(() => {
    const opened = current ? current.id : ""

    setEditable(null)
    setCurrent(null)
    onQuestionClosed && onQuestionClosed()

    // карточку показываем на доске: после перехода по ссылке искать её в колонке не придётся
    opened && revealCard(opened)
  }, [current, onQuestionClosed, revealCard])

  const submit = useCallback(
    async (value: SaveQuestion) => {
      store.setLoading(true)
      const success = await store.save(value)
      store.setLoading(false)

      success && closeDialog()

      return success
    },
    [store, closeDialog]
  )

  const saveColumns = useCallback(
    async (value: SaveColumn[]) => {
      store.setLoading(true)
      const success = await store.saveColumns(value)
      store.setLoading(false)

      return success
    },
    [store]
  )

  const savePortfolio = useCallback(
    async (value: { id: string; name: string; color: string; memberIds: string[] }) => {
      store.setLoading(true)
      const success = await store.savePortfolio(value)
      store.setLoading(false)

      return success
    },
    [store]
  )

  const removePortfolio = useCallback(
    async (id: string) => {
      store.setLoading(true)
      const success = await store.removePortfolio(id)
      store.setLoading(false)

      return success
    },
    [store]
  )

  const removeQuestion = useCallback(
    async (value: SaveQuestion) => {
      const success = await store.remove([{ id: value.id }])
      success && closeDialog()
    },
    [store, closeDialog]
  )

  /** Быстрое добавление карточки прямо в колонке: только заголовок */
  const quickAdd = useCallback(
    async (columnId: string, title: string) => {
      const empty = store.emptyQuestion(columnId)

      return store.save({
        ...mapQuestion(empty, gip),
        title,
        orderId: empty.orderId || EMPTY_OBJECT_ID,
        columnId,
      })
    },
    [store, gip]
  )

  const onDragStart = useCallback(
    (start: { draggableId: string }) => {
      setDragging(store.findCard(start.draggableId))
    },
    [store]
  )

  const onDragEnd = useCallback(
    (result: DropResult) => {
      setDragging(null)

      if (!result.destination) {
        return
      }

      const { droppableId, index } = result.destination

      if (droppableId === result.source.droppableId && index === result.source.index) {
        return
      }

      // позицию задаём соседом сверху: показана только часть карточек колонки
      const page = store.page(droppableId)
      const target = (page ? page.items : []).filter((q) => q.id !== result.draggableId)
      const after = index > 0 ? target[index - 1] : null

      const move = {
        questionId: result.draggableId,
        columnId: droppableId,
        sortOrder: index,
        afterId: after ? after.id : "",
      }

      // отправлять на проверку за коллег — осознанное решение, поэтому спрашиваем
      const card = store.findCard(result.draggableId)

      if (
        droppableId === SystemColumns.review &&
        card &&
        card.executorsCount > 1 &&
        card.readyCount < card.executorsCount
      ) {
        setReviewConfirm({ card, move })
        return
      }

      store.move(move.questionId, move.columnId, move.sortOrder, move.afterId)
    },
    [store]
  )

  const confirmReview = useCallback(() => {
    if (!reviewConfirm) {
      return
    }

    const { move } = reviewConfirm

    setReviewConfirm(null)
    store.move(move.questionId, move.columnId, move.sortOrder, move.afterId)
  }, [reviewConfirm, store])

  const loadMore = useCallback((columnKey: string) => store.loadMore(columnKey), [store])

  /** Перенос на другую доску: сначала доска с колонкой, потом судьба подзаданий */
  const moveToBoard = useCallback(
    async (card: QuestionCardType, boardId: string, columnId: string, withChildren: boolean) => {
      store.setLoading(true)
      await store.moveToBoard(card.id, boardId, columnId, withChildren)
      store.setLoading(false)
    },
    [store]
  )

  const startMove = useCallback(
    async (boardId: string, columnId: string) => {
      const card = moveTarget

      if (!card) {
        return
      }

      setMoveTarget(null)

      // подзадания уезжают вместе с заданием только по согласию
      if (card.childrenCount > 0) {
        setChildrenConfirm({ card, boardId, columnId })
        return
      }

      await moveToBoard(card, boardId, columnId, false)
    },
    [moveTarget, moveToBoard]
  )

  /** Отметка выполнения: за всех спрашиваем, за себя ставим сразу */
  const markReady = useCallback(
    async (card: QuestionCardType) => {
      store.setLoading(true)
      await store.setReadiness(card.id, true, "", card.canManageReadiness)
      store.setLoading(false)
    },
    [store]
  )

  const removeCard = useCallback(
    async (card: QuestionCardType) => {
      store.setLoading(true)
      await store.remove([{ id: card.id }])
      store.setLoading(false)
    },
    [store]
  )

  // на другую доску переносим, когда сотруднику доступна хотя бы одна ещё
  const otherBoards = store.portfolios.filter((p) => p.id !== store.portfolioId).length
    + (store.portfolioId ? 1 : 0)

  /** Ссылка на задание одна и та же, откуда бы карточку ни открыли */
  const shareLink = useCallback(
    (id: string) => {
      const link = window.location.origin + routes.orders.orderQuestionDetails(id)

      copyToClipboard(link, { format: "text/plain" })
        ? store.notify.success("Ссылка на задание скопирована")
        : store.notify.error("Не удалось скопировать ссылку")
    },
    [store]
  )

  const cardActions = useCallback(
    (card: QuestionCardType, confirmRemoving: (card: QuestionCardType) => void): ContextMenuAction[] => {
      const actions: ContextMenuAction[] = [
        {
          key: "edit",
          label: card.canEditQuestion ? "Изменить" : "Открыть",
          icon: "general-edit",
          onClick: () => openQuestion(card),
        },
        {
          key: "share",
          label: "Поделиться ссылкой",
          icon: "general-copy",
          onClick: () => shareLink(card.id),
        },
      ]

      // отмечать есть что, пока на карточке горят галочки готовности и закрыты не все:
      // у заданий самому себе счётчика нет, а закрытые отмечать уже незачем
      const unfinished = card.executorsCount > 0 && card.readyCount < card.executorsCount

      // распорядитель отмечает сразу за всех, исполнитель — за себя
      const ownReadiness = card.isExecutor && !card.ownReady

      if (unfinished && (card.canManageReadiness || ownReadiness)) {
        actions.push({
          key: "ready",
          label: "Отметить выполненным",
          icon: "general-check",
          success: true,
          onClick: () => (card.canManageReadiness ? setReadinessConfirm(card) : markReady(card)),
        })
      }

      if (card.canMoveToBoard && otherBoards > 0) {
        actions.push({
          key: "board",
          label: "Перенести на доску",
          icon: "general-import",
          onClick: () => setMoveTarget(card),
        })
      }

      if (card.canRemoveQuestion) {
        actions.push({
          key: "remove",
          label: "Удалить",
          icon: "general-trash",
          danger: true,
          onClick: () => confirmRemoving(card),
        })
      }

      return actions
    },
    [openQuestion, markReady, otherBoards, shareLink]
  )

  /** Задание, открытое адресом: следим за ним, чтобы не открывать одно и то же дважды */
  const linked = useRef("")

  useEffect(() => {
    const id = linkedQuestionId || ""

    // ждём доску: карточка должна открываться поверх колонок, а не поверх заглушки загрузки
    if (store.boardLoading) {
      return
    }

    // задание из ссылки открываем, как только доска готова
    if (id && id !== linked.current) {
      linked.current = id

      if (!current || current.id !== id) {
        openQuestion({ id })
      }

      return
    }

    // «Назад» в браузере убрал задание из адреса — закрываем карточку
    if (!id && linked.current) {
      linked.current = ""
      editable && closeDialog()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedQuestionId, store.boardLoading])

  // на общей доске свои колонки разрешает настройка, на личной их заводит владелец
  const canAddColumn = store.canManageColumns && (store.portfolioId ? true : store.customColumnsEnabled)

  // во вкладке договора шапка досок нужна, только если есть куда переключаться:
  // сервер оставляет там доски с заданиями пользователя по этому договору
  const showPortfolioBar = hideProject ? store.portfolios.length > 0 : store.portfolioBarVisible

  return (
    <div className={styles.board}>
      {store.loading && <Loader active />}

      {showPortfolioBar && (
        <PortfolioBar
          portfolios={store.portfolios}
          currentId={store.portfolioId}
          // новую доску заводят на самом трекере: заведённая из вкладки договора
          // сразу же из неё и исчезнет — в ней нет заданий по этому договору
          canCreate={store.canCreatePortfolio && !hideProject}
          onSelect={store.selectPortfolio}
          onCreate={() => setPortfolioDialog({ open: true, value: null })}
          onEdit={(portfolio) => setPortfolioDialog({ open: true, value: portfolio })}
        />
      )}

      <div className={styles.toolbar}>
        <BoardFilters
          compact={compact}
          orders={store.orders}
          employers={store.employee}
          // фильтр по проектам нужен на любой доске: скрыт только во вкладке договора,
          // где проект задан контекстом
          hideProject={hideProject}
          members={store.portfolioMembers}
          query={store.query}
          orderIds={store.filtersOrderIds.slice()}
          authorIds={store.filtersAuthorIds.slice()}
          employerIds={store.filtersEmployerIds.slice()}
          implementerIds={store.filtersImplementerIds.slice()}
          setQuery={searchAll ? openSearch : store.setQuery}
          setOrderIds={store.setOrderIds}
          setAuthorIds={store.setAuthorIds}
          setEmployerIds={store.setEmployerIds}
          setImplementerIds={store.setImplementerIds}
          rightTools={
            <PlanrButton
              type="graybtn"
              icon="general-print"
              round={true}
              title="Печать"
              onClick={store.printQuestions}
            />
          }
        >
          {canAddQuestion && (
            <PlanrButton type="primary" size="small" icon="general-plus-small" onClick={() => openNew()}>
              Задание
            </PlanrButton>
          )}
          {(store.canManageColumns || store.customColumnsEnabled) && (
            <PlanrButton type="graybtn" round={true} title="Колонки доски" onClick={() => setColumnsDialog(true)}>
              <ColumnsIcon />
            </PlanrButton>
          )}
        </BoardFilters>

        {/* под строкой фильтров, слева под «+ Задание»: те же задания списком по всем доскам */}
        {searchAll && (
          <div className={styles.viewRow}>
            <ListViewToggle onClick={() => navigate(routes.orders.orderQuestionsList())} />
          </div>
        )}
      </div>

      {/* доска отфильтрована одним заданием из уведомления: кнопка сброса стоит
          по центру экрана поверх колонок, чтобы её не искали в тулбаре.
          Во время загрузки не показываем — сбрасывать ещё нечего */}
      {onShowAll && !showAllPressed && !store.loading && !store.boardLoading && (
        <div className={styles.showAll}>
          <PlanrButton
            type="greenish"
            onClick={() => {
              setShowAllPressed(true)
              onShowAll()
            }}
          >
            Отобразить все
          </PlanrButton>
        </div>
      )}

      {store.boardLoading ? (
        <div className={styles.loading}>
          <LocalLoader active={true} />
        </div>
      ) : (
      <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <div className={styles.columns}>
          {columns.map((column) => (
            <BoardColumnView
              key={column.key}
              column={column}
              page={store.page(column.key)}
              // проект на карточке показываем и на личных досках, если он указан
              hideProject={hideProject}
              currentUserId={currentUserId}
              canAdd={!!canAddQuestion}
              tagColors={tagColors}
              dropDisabled={
                isDropDisabled(column.key) ||
                (column.key === SystemColumns.done && !!dragging && !dragging.canCompleteQuestion)
              }
              revealedId={revealed}
              compact={compact}
              onOpen={openQuestion}
              onMenu={(card, x, y) => setMenu({ card, x, y })}
              onQuickAdd={quickAdd}
              onLoadMore={loadMore}
            />
          ))}

          {canAddColumn && (
            <AddColumn
              index={columns.length}
              empty={columns.length === 0}
              onAdd={(name, color) => store.addColumn(name, color)}
            />
          )}

          {columns.length === 0 && !canAddColumn && (
            <div className={styles.noColumns}>
              {store.portfolioId
                ? "На доске пока нет колонок, их заводит её владелец"
                : "Колонки доски не настроены"}
            </div>
          )}
        </div>
      </DragDropContext>
      )}

      {editable && current && (
        <QuestionDialog
          value={editable}
          question={current}
          columns={columns}
          tags={store.tags}
          employee={store.employee}
          orders={store.orders}
          baseUrl={store.baseUrl}
          // проект выбирают и на личных досках: скрываем селект только во вкладке договора,
          // где проект задан контекстом
          hideProject={hideProject}
          currentUserId={currentUserId}
          canAddQuestion={canAddQuestion && canManage}
          onClose={closeDialog}
          onShare={shareLink}
          onSubmit={submit}
          onRemove={removeQuestion}
          onAddMessage={(questionId: string, message: MessageType, fileIds?: string[]) =>
            store.saveMessage(questionId, message, fileIds)
          }
          boardName={store.currentPortfolio ? store.currentPortfolio.name : "Задания по проектам"}
          onDeleteMessage={(questionId: string, messageId: string) => store.deleteMessage(questionId, messageId)}
          onCreateChild={(parent) => openNew(SystemColumns.inWork, parent)}
          onOpenQuestion={openQuestion}
          onSetReadiness={(questionId: string, ready: boolean, employerId?: string) =>
            store.setReadiness(questionId, ready, employerId)
          }
          portfolioMembers={store.portfolioMembers}
          upload={store.uploadFile}
        />
      )}

      {reviewConfirm && (
        <ReviewConfirmDialog
          card={reviewConfirm.card}
          onConfirm={confirmReview}
          onCancel={() => setReviewConfirm(null)}
        />
      )}

      {columnsDialog && (
        <ColumnsDialog
          columns={columns}
          customEnabled={store.customColumnsEnabled || !!store.portfolioId}
          onClose={() => setColumnsDialog(false)}
          onSubmit={saveColumns}
        />
      )}

      {/* подтверждение живёт вне меню: меню закрывается сразу по клику по пункту */}
      <RemoveConfirmation<QuestionCardType>
        what={(card) => `задание ${card ? card.label : ""}`}
        onConfirmed={(card) => card && removeCard(card)}
      >
        {({ confirmRemoving }) =>
          menu && (
            <ContextMenu
              x={menu.x}
              y={menu.y}
              actions={cardActions(menu.card, confirmRemoving)}
              onClose={() => setMenu(null)}
            />
          )
        }
      </RemoveConfirmation>

      {readinessConfirm && (
        <ReadinessConfirmDialog
          card={readinessConfirm}
          reviewColumn={columns.find((c) => c.key === SystemColumns.review)?.name ?? ""}
          onConfirm={() => {
            const card = readinessConfirm

            setReadinessConfirm(null)
            markReady(card)
          }}
          onCancel={() => setReadinessConfirm(null)}
        />
      )}

      {moveTarget && (
        <MoveToBoardDialog
          card={moveTarget}
          portfolios={store.portfolios}
          currentBoardId={store.portfolioId}
          canUseProjectsBoard={!!canAddQuestion}
          onLoadColumns={store.loadBoardColumns}
          onSubmit={startMove}
          onCancel={() => setMoveTarget(null)}
        />
      )}

      {childrenConfirm && (
        <MoveChildrenDialog
          card={childrenConfirm.card}
          onLoad={store.questionChildren}
          onConfirm={(withChildren) => {
            const request = childrenConfirm

            setChildrenConfirm(null)
            moveToBoard(request.card, request.boardId, request.columnId, withChildren)
          }}
          onCancel={() => setChildrenConfirm(null)}
        />
      )}

      {portfolioDialog.open && (
        <PortfolioDialog
          portfolio={portfolioDialog.value}
          employee={store.employee}
          onClose={() => setPortfolioDialog({ open: false, value: null })}
          onSubmit={savePortfolio}
          onRemove={removePortfolio}
        />
      )}
    </div>
  )
})

/** Открытое меню карточки: координаты берём из правого клика */
interface CardMenu {
  card: QuestionCardType
  x: number
  y: number
}

/** Перенос на доску: доска с колонкой выбраны, осталось решить судьбу подзаданий */
interface MoveRequest {
  card: QuestionCardType
  boardId: string
  columnId: string
}

/** Отложенный перенос: ждём подтверждения отправки на проверку за всех */
interface ReviewConfirm {
  card: QuestionCardType
  move: { questionId: string; columnId: string; sortOrder: number; afterId: string }
}

interface QuestionsBoardProps {
  store: OrderQuestionStoreType
  canManage: boolean
  canAddQuestion?: boolean
  hideProject?: boolean
  currentUserId: string
  orderGip?: string | null
  onShowAll?: () => void
  /** Задание из адреса страницы: доска открывает его карточку сама */
  linkedQuestionId?: string
  /** Открытое задание показываем в адресе: ссылкой на него можно поделиться */
  onQuestionOpened?: (id: string) => void
  onQuestionClosed?: () => void
  /** Доска в узком виджете главной: колонки уже, карточки плотнее, фильтры свёрнуты */
  compact?: boolean
}
